import React, { useState, useRef, useEffect } from "react";
import { useShallow } from "zustand/react/shallow";

import { Button, FormControl, FormControlLabel, RadioGroup, Radio, TextField } from "@mui/material";
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from "@mui/material";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode, { type Code } from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";

import { useAuthStore } from "@user/store/auth_store";

import "./popup_audit_manage.scoped.scss";

interface State {
  stockCheckDetailNo: string
}

const defState = {
  stockCheckDetailNo: ""
};

interface Info {
  stockCheckNo: string,
  detailInfos: any[]
}

const defInfo = {
  stockCheckNo: "",
  detailInfos: []
};

const validationMsg = [
  {id: "actStatus", type: "NotBlank", msg: "조치상태를 선택해 주세요."},
  {id: "actContent", type: "NotBlank", msg: "조치내용을 입력해 주세요."}
];

function PopupAuditManage(props: any) {
  const auth = useAuthStore(useShallow((state) => state.getAuth(props.id)));
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [info, setInfo] = useState<Info>(kutil.deepCopy(defInfo));

  useEffect(() => {
    init();
  }, []);

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await searchInfo();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
    state.stockCheckDetailNo = props.data.stockCheckDetailNo;
    setState(state);
  }

  async function searchInfo() {
    try {
      const data = await kutil.sendGet("/api/stock/audit_manage_detail.data?mode=searchDetail", {stockCheckDetailNo: state.stockCheckDetailNo}, {});
      setInfo({
        ...data.info,
        detailInfos: data.detailInfos,
      });
    } catch(e) {
      console.log(e);
    }
  }

  async function closePopup() {
    props.onClose();
  }

  async function save() {
    const isValid = await kutil.checkValidation4list(info.detailInfos, validationMsg);
    if (isValid == false) {
      return;
    }

    if (await kutil.confirm("저장하시겠습니까?") == false) {
      return false;
    }

    try {
      const data = await kutil.sendPost("/api/stock/audit_manage_detail.proc?mode=updateActStatus", info, {
        validationMsg: validationMsg
      });
      await kutil.alert("저장하였습니다.");
      props.onClose({isSuccess: true});
    } catch(e) {
      console.log(e);
    }
  }

  return (
    <>
      {/* 팝업 */}
      <Dialog
        sx={{ "& .MuiDialog-container": { "& .MuiPaper-root": { width: "100%", maxWidth: "1600px", }, }, }}
        aria-labelledby="customized-dialog-title"
        open={props.isShow}
      >
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
          재고실사 조치 내용
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={closePopup}
          sx={(theme) => ({
            position: "absolute",
            right: 36,
            top: 24,
            color: theme.palette.grey[500],
          })}
        >
        </IconButton>
        <DialogContent>
          {/* 팝업 내용 */}
          <div className="popup-contents">
            {/* 재고실사 정보 */}
            <div className="table-wrap">
              <table className="tbl-list">
                <thead>
                  <tr>
                    <th scope="col">Location</th>
                    <th scope="col">SKU No</th>
                    <th scope="col">관리구분</th>
                    <th scope="col">창고</th>
                    <th scope="col">P/N</th>
                    <th scope="col">TR No</th>
                    <th scope="col">S/N</th>
                    <th scope="col">KWD</th>
                    <th scope="col">CTG</th>
                    <th scope="col">Stock Qty</th>
                    <th scope="col">Check Qty</th>
                    <th scope="col">Diff Qty</th>
                    <th scope="col">UOM</th>
                    <th scope="col">실사자</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{ valueFormatter.locationId(info.detailInfos[0]?.locationId) }</td>
                    <td>{ info.detailInfos[0]?.skuNoEx }</td>
                    <td>{commonCode.getCodeValue("mngType", info.detailInfos[0]?.mngType)}</td>
                    <td>{commonCode.getCodeValue("wh", info.detailInfos[0]?.wh)}</td>
                    <td>{ info.detailInfos[0]?.pn }</td>
                    <td>{ info.detailInfos[0]?.trNo }</td>
                    <td>{ info.detailInfos[0]?.sn }</td>
                    <td>{ info.detailInfos[0]?.kwd }</td>
                    <td>{ info.detailInfos[0]?.ctg }</td>
                    <td>{ valueFormatter.number(info.detailInfos[0]?.stockQty) }</td>
                    <td>{ valueFormatter.number(info.detailInfos[0]?.checkQty) }</td>
                    <td>{ valueFormatter.number(info.detailInfos[0]?.diffQty) }</td>
                    <td>{ info.detailInfos[0]?.uom }</td>
                    <td>{ info.detailInfos[0]?.checkUserName }</td>
                  </tr>
                  <tr>
                    <th scope="col" colSpan={14}>Remark</th>
                  </tr>
                  <tr>
                    <td colSpan={14} className="ta-left">
                      { info.detailInfos[0]?.remark }
                      </td>
                  </tr>
                </tbody>
              </table>
            </div>
            {/*// 재고실사 정보 */}
            {/* 서브 타이틀 */}
            <div className="sub-title-area">
              <p>조치내용</p>
            </div>
            {/* 조치 내용 */}
            <div className="table-wrap emp">
              <table>
                <colgroup>
                  <col style={{ width: "8%" }} />
                  <col style={{ width: "17%" }} />
                  <col style={{ width: "8%" }} />
                  <col style={{ width: "17%" }} />
                  <col style={{ width: "8%" }} />
                  <col style={{ width: "17%" }} />
                  <col style={{ width: "8%" }} />
                  <col style={{ width: "17%" }} />
                </colgroup>
                <tbody>
                  <tr>
                    <th scope="row">조치상태</th>
                    <td>
                      <div className="form-base">
                        <FormControl>
                          <RadioGroup
                            row
                            value={info.detailInfos[0]?.actStatus ?? ""}
                            onChange={(e) => {
                              setInfo(prev => ({
                                ...prev,
                                detailInfos: prev.detailInfos.map((item, idx) =>
                                  idx === 0 ? { ...item, actStatus: e.target.value } : item
                                ),
                              }));
                            }}
                          >
                            {(commonCode.actStatus as any[]).map((code) => (
                              <FormControlLabel key={code.code} value={code.code} control={<Radio size="small" />} label={code.value} />
                            ))}
                          </RadioGroup>
                        </FormControl>
                      </div>
                    </td>
                    <th scope="row">조치일</th>
                    <td>
                      { kutil.date2string(info.detailInfos[0]?.actDate, "yyyy-MM-dd HH:mm:ss") }
                    </td>
                    <th scope="row">검토자</th>
                    <td>
                      { info.detailInfos[0]?.actUserName }
                    </td>
                    <td colSpan={2}></td>
                  </tr>
                  <tr>
                    <th scope="row">조치내용</th>
                    <td colSpan={7}>
                      <TextField
                        value={info.detailInfos[0]?.actContent ?? ""}
                        onChange={(e) => {
                          setInfo(prev => ({
                            ...prev,
                            detailInfos: prev.detailInfos.map((item, idx) =>
                              idx === 0 ? { ...item, actContent: e.target.value } : item
                            ),
                          }));
                        }}
                        multiline
                        rows={3}
                        fullWidth 
                      />
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">과부족 원인</th>
                    <td colSpan={7}>
                      <TextField
                        value={info.detailInfos[0]?.actCause ?? ""}
                        onChange={(e) => {
                          setInfo(prev => ({
                            ...prev,
                            detailInfos: prev.detailInfos.map((item, idx) =>
                              idx === 0 ? { ...item, actCause: e.target.value } : item
                            ),
                          }));
                        }}
                        multiline
                        rows={3}
                        fullWidth 
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            {/*// 조치 내용 */}
          </div>
          {/*// 팝업 내용 */}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" autoFocus onClick={closePopup}>
            취소
          </Button>
          { auth.write &&
          <Button variant="contained" autoFocus onClick={save}>
            저장
          </Button>
          }
        </DialogActions>
      </Dialog>
      {/*// 팝업 */}
    </>
  );
}
export default React.memo(PopupAuditManage);
